export type Track = 'dashboard' | 'visualizer'

export interface QuizQuestion {
  id: string
  question: string
  options: string[]
  correctIndex: number
}

export interface Lesson {
  id: string
  title: string
  description: string
  duration: string // e.g., "3:45"
  videoUrl?: string
  thumbnailUrl?: string
  quiz?: QuizQuestion[]
}

export interface Course {
  id: string
  title: string
  description: string
  icon: string // Lucide icon name
  color: string // Tailwind color prefix (e.g., 'blue', 'emerald')
  track: Track
  lessons: Lesson[]
}

export interface QuickLink {
  id: string
  title: string
  courseId: string
  lessonId: string
}

export const courses: Course[] = [
  // Dashboard Track
  {
    id: 'dash-basics',
    title: 'Dashboard Essentials',
    description:
      'Learn how to navigate the ResVR builder dashboard, manage projects, and view analytics.',
    icon: 'LayoutDashboard',
    color: 'blue',
    track: 'dashboard',
    lessons: [
      {
        id: 'dash-1',
        title: 'Navigating the Dashboard',
        description:
          'A quick tour of the main dashboard, key metrics, and where to find everything you need.',
        duration: '2:15',
        thumbnailUrl:
          'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800&h=450',
      },
      {
        id: 'dash-2',
        title: 'Managing Projects & Models',
        description:
          'How to set up new projects, add models, and organize your communities.',
        duration: '4:30',
        thumbnailUrl:
          'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800&h=450',
        quiz: [
          {
            id: 'q1',
            question: 'Where do you go to add a new model to a project?',
            options: [
              'The Analytics tab',
              'The Models page',
              'The Settings menu',
              'The Support page',
            ],
            correctIndex: 1,
          },
        ],
      },
      {
        id: 'dash-3',
        title: 'Understanding Analytics',
        description:
          'Deep dive into buyer behavior, popular upgrades, and revenue tracking.',
        duration: '5:45',
        thumbnailUrl:
          'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800&h=450',
      },
    ],
  },
  {
    id: 'dash-options',
    title: 'Options & Packages Mastery',
    description:
      'Everything you need to know about configuring upgrades, structural options, and design packages.',
    icon: 'Layers',
    color: 'purple',
    track: 'dashboard',
    lessons: [
      {
        id: 'opt-1',
        title: 'Creating Individual Options',
        description:
          'Step-by-step guide to adding new upgrades, setting prices, and assigning them to rooms.',
        duration: '6:20',
        thumbnailUrl:
          'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&q=80&w=800&h=450',
      },
      {
        id: 'opt-2',
        title: 'Building Design Packages',
        description:
          'How to bundle multiple options into cohesive design packages for your buyers.',
        duration: '5:10',
        thumbnailUrl:
          'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=800&h=450',
        quiz: [
          {
            id: 'q1',
            question: 'Can an option belong to multiple packages?',
            options: [
              'Yes, always',
              'No, options are exclusive to one package',
              'Only if it is a structural option',
              'Only for premium packages',
            ],
            correctIndex: 0,
          },
          {
            id: 'q2',
            question:
              'What happens when you update the price of an option inside a package?',
            options: [
              'The package price updates automatically',
              'You must manually update the package price',
              'The option is removed from the package',
              'It creates a duplicate option',
            ],
            correctIndex: 0,
          },
        ],
      },
    ],
  },

  // Visualizer Track
  {
    id: 'vis-intro',
    title: 'Getting Started with the Visualizer',
    description:
      'Learn the basic controls, navigation, and core features of the Unreal Engine visualizer app.',
    icon: 'MonitorPlay',
    color: 'emerald',
    track: 'visualizer',
    lessons: [
      {
        id: 'vis-1',
        title: 'Basic Navigation & Controls',
        description:
          'How to move around the virtual home, use the camera, and interact with objects.',
        duration: '3:45',
        thumbnailUrl:
          'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&q=80&w=800&h=450',
      },
      {
        id: 'vis-2',
        title: 'Making Selections in VR',
        description:
          'Guide buyers through selecting finishes, cabinets, and structural changes in real-time.',
        duration: '7:15',
        thumbnailUrl:
          'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&q=80&w=800&h=450',
        quiz: [
          {
            id: 'q1',
            question:
              'How do you open the materials menu for a specific surface?',
            options: [
              'Press M on the keyboard',
              'Click directly on the surface',
              'Use the main top menu',
              'Right-click the floor',
            ],
            correctIndex: 1,
          },
        ],
      },
    ],
  },
  {
    id: 'vis-advanced',
    title: 'Advanced Presentation Techniques',
    description:
      'Tips and tricks for sales teams to deliver "wow" moments and close more upgrades.',
    icon: 'Sparkles',
    color: 'amber',
    track: 'visualizer',
    lessons: [
      {
        id: 'vis-3',
        title: 'Lighting & Time of Day',
        description:
          'Showcasing homes in different lighting conditions to highlight specific features.',
        duration: '4:20',
        thumbnailUrl:
          'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&q=80&w=800&h=450',
      },
      {
        id: 'vis-4',
        title: 'Saving & Sharing Configurations',
        description:
          "How to save a buyer's selections and generate a summary PDF directly from the app.",
        duration: '3:50',
        thumbnailUrl:
          'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80&w=800&h=450',
      },
    ],
  },
]

export const quickLinks: QuickLink[] = [
  {
    id: 'ql-1',
    title: 'How do I add a new structural option?',
    courseId: 'dash-options',
    lessonId: 'opt-1',
  },
  {
    id: 'ql-2',
    title: 'Keyboard shortcuts for the Visualizer',
    courseId: 'vis-intro',
    lessonId: 'vis-1',
  },
  {
    id: 'ql-3',
    title: 'Where can I see my most popular upgrades?',
    courseId: 'dash-basics',
    lessonId: 'dash-3',
  },
  {
    id: 'ql-4',
    title: 'How do I generate a PDF summary for a buyer?',
    courseId: 'vis-advanced',
    lessonId: 'vis-4',
  },
]
